import type { ThemeAIPersonalDataDecision, ThemeAIScreening } from '../types/gotypes'

// Plain words for the personal data flag (#494), in one place so the offer,
// the review and the results line say the same thing. A flag only: nothing
// here, or on the server, changes an answer's text.

export const PERSONAL_DATA_CODE = '98'
export const PERSONAL_DATA_NAME = 'Contains personal data'

const CATEGORY_LABELS: Record<string, string> = {
  email: 'Email address',
  phone: 'Phone number',
  card_number: 'Card number',
  ni_number: 'National Insurance number',
  ip_address: 'IP address',
  postcode: 'Postcode',
  name: 'Name',
  address: 'Address',
  id_number: 'ID number',
  other: 'Other',
}

export const personalDataCategoryLabel = (category: string): string => CATEGORY_LABELS[category] ?? category

/** Categories in the order the review lists them. */
export const PERSONAL_DATA_CATEGORY_ORDER = Object.keys(CATEGORY_LABELS)

/** How many flagged answers a run would code to 98: flagged, less those the user keeps. */
export const personalDataFlaggedCount = (screening: ThemeAIScreening): number =>
  Math.max(0, (screening.personal_data_count ?? 0) - (screening.personal_data_kept_count ?? 0))

export const personalDataCountLabel = (count: number): string =>
  `${count.toLocaleString()} answer${count === 1 ? '' : 's'} with personal data`

/** The decision as a short phrase for a status line. */
export const personalDataDecisionWords = (decision: ThemeAIPersonalDataDecision | undefined): string => {
  switch (decision) {
    case 'keep': return 'not flagging them'
    case 'code': return `coding them as “${PERSONAL_DATA_NAME.toLowerCase()}”`
    default: return 'no decision yet'
  }
}

/**
 * The offer's line for the check in use: what it found, or that it was made
 * before personal data was looked for. Undefined when there is nothing to say.
 */
export const personalDataOfferLine = (screening: ThemeAIScreening): string => {
  if (!screening.personal_data_checked) return 'This check was made before answers were checked for personal data. Check again to look for it.'
  const flagged = personalDataFlaggedCount(screening)
  if ((screening.personal_data_count ?? 0) === 0) return 'No personal data found.'
  return `${personalDataCountLabel(flagged)} · ${personalDataDecisionWords(screening.personal_data_decision)}.`
}

/** The run line on Results: how many answers the run flagged, or nothing to say. */
export const personalDataRunLine = (decision: ThemeAIPersonalDataDecision | undefined, count: number | undefined): string | undefined => {
  if (!count || decision !== 'code') return undefined
  return `${count.toLocaleString()} flagged as containing personal data`
}
